import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentOrganization } from 'src/store/organization';
import { formatDateTime } from 'src/utils/date';
import { apiBulkDeleteContents, apiGetAllContents } from 'src/utils/journeyApi';
import { generateColumn } from '../table/LegacyTable';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';
import { useFilterBarParams } from '../filter-bar/use-params';
import { TableSortableColumnHeader, TableSortableColumnHeaderOrderType } from '../table/sortable-column-header';
import { DashboardHomeJourneyListHeaderSortType } from 'src/dashboard/home/journeys/list';
import { DataTable, DataTableProps } from '../table/data-table';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { Content } from 'src/common/interfaces/content.interface';
import { LibraryHoverableCardSmall } from './hoverable-content-card-small';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { LibraryContentCardMenuActions } from './content-card/menu/actions';
import { TooltipComponent } from 'src/common/tooltip/component';
import { Tag } from 'src/common/interfaces/tag.interface';
import { Checkbox } from 'src/common/form/checkbox';
import ActionBar from './action-bar';

type LibraryContentListProps = {
  refetchRef?: MutableRefObject<() => void>;
  containerClassName?: string;
  tableClassName?: string;
  columnClasses?: string[];
  isBox?: boolean;
  contentContainerClassName?: string;
  loadingClassName?: string;
  onChooseExisting?: (content: Content) => void;
  editable?: boolean;
  removable?: boolean;
} & Pick<DataTableProps, 'disableRowCursorPointer'>;

export const LibraryContentList = ({
  refetchRef,
  tableClassName = '',
  containerClassName = '',
  columnClasses = [],
  contentContainerClassName = '',
  disableRowCursorPointer,
  loadingClassName = 'my-40',
  onChooseExisting,
  editable = false,
  isBox,
  removable = false,
}: LibraryContentListProps) => {
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const { currentOrganization } = useCurrentOrganization();
  const scrollTopOnceFetchDoneRef = useRef(false);
  const hideLoadingSpinnerRef = useRef(false);

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const {
    filterBarParams: { search_term, page, sort_by, sort_order, filters },
    updatePage,
    updateSort,
  } = useFilterBarParams({ sort_by: 'updated_at' });

  const [pages, setPages] = useState(1);

  const onFetchContentsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setContents(response.contents);
      setPages(response.pages);
      setLoading(false);
    },
    [currentOrganization.id, search_term, page, sort_by, sort_order]
  );

  const updateContentInList = (content: Content) => {
    setContents((contents) => {
      const index = contents.findIndex((c) => c.id === content.id);
      if (index === -1) {
        return contents;
      }
      const newContents = [...contents];
      newContents[index] = content;
      return newContents;
    });
    setSuccessNotification('Content tags updated');
  };

  const onTagsUpdate = (content: Content) => {
    updateContentInList(content);
  };

  const onContentDeleted = (contentId: Content['id']) => {
    setContents((contents) => {
      return contents.filter((c) => c.id !== contentId);
    });
    setSuccessNotification('Content deleted');
  };

  const fetchContents = useCallback(async () => {
    const params = {
      page: page,
      ...(search_term && { search_str: search_term }),
      ...(sort_by && { sort_by }),
      ...(sort_by && sort_order !== 'default' && { sort_order }),
      ...(filters?.tag_ids && { tag_ids: filters?.tag_ids?.join(',') }),
      ...(isBox && { type: 'box' }),
    };

    try {
      if (!hideLoadingSpinnerRef.current) {
        setLoading(true);
      }
      const response = await apiGetAllContents(currentOrganization.id, params);
      if (onFetchContentsRef.current) {
        if (scrollTopOnceFetchDoneRef.current) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        onFetchContentsRef.current(response, [currentOrganization.id, search_term, page, sort_by, sort_order]);
      }
    } catch (e) {
      console.error(e);
    }
  }, [onFetchContentsRef, currentOrganization.id, search_term, page, sort_by, sort_order, filters, isBox]);

  useEffect(() => {
    fetchContents();
    if (refetchRef) {
      hideLoadingSpinnerRef.current = true;
      refetchRef.current =
        page === 1
          ? fetchContents
          : () => {
              updatePage(1);
            };
    }
  }, [fetchContents, page, filters]);

  const onTableHeaderClick = (sortStack: DashboardHomeJourneyListHeaderSortType) => {
    hideLoadingSpinnerRef.current = false;
    updateSort(sortStack.sortName, sortStack.orderBy);
  };
  const selectedRowsRef = useRef(selectedRows);

  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  const toggleRowSelection = (contentId: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contentId)) {
        newSet.delete(contentId);
      } else {
        newSet.add(contentId);
      }
      return newSet;
    });
  };

  const handleHeaderSelectAllRowsChange = (data: any) => {
    if (selectedRowsRef.current.size === data.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(contents.map((c) => c.id)));
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await apiBulkDeleteContents(Array.from(selectedRows));
      setSuccessNotification(`Successfully deleted ${result.deleted_count} item(s)`);
      setSelectedRows(new Set());
      fetchContents();
    } catch (error) {
      console.error('Error deleting contents:', error);
      setErrorNotification('Failed to delete selected items');
    }
  };

  const onClearSelection = () => setSelectedRows(new Set());

  const libraryColumns = useMemo(
    () => {
      const columns = [
        generateColumn({
          Header: ({ data }: { data: any }) => {
            return (
              <Checkbox
                isBedrock
                className='h-10 rounded-md'
                checked={data.length > 0 && selectedRowsRef.current.size === data.length}
                onChange={() => handleHeaderSelectAllRowsChange(data)}
              />
            );
          },
          accessor: 'select',
          width: 40,
          Cell: (data: any) => {
            return (
              <Checkbox
                isBedrock
                className='h-10 rounded-md'
                checked={selectedRowsRef.current.has(data.row.original.id)}
                onChange={() => toggleRowSelection(data.row.original.id)}
              />
            );
          },
        }),
        generateColumn({
          Header: (
            <TableSortableColumnHeader
              isActive={sort_by === 'name'}
              activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
              accessor='name'
              onClick={onTableHeaderClick}
              title='Name'
            />
          ),
          accessor: 'name',
          width: 300,
          Cell: ({ row }: { row: any }) => {
            return (
              <LibraryHoverableCardSmall
                editable={editable}
                onUpdateContent={updateContentInList}
                content={row.original}
              />
            );
          },
        }),
        generateColumn({
          Header: (
            <TableSortableColumnHeader
              isActive={sort_by === 'updated_at'}
              activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
              accessor='updated_at'
              onClick={onTableHeaderClick}
              title='Updated'
            />
          ),
          accessor: 'updated_at',
          width: 132,
          Cell: ({ row }: { row: any }) => (
            <span className='text-bedrock-p text-bedrock-black'>
              {row.original.updated_at ? formatDateTime(row.original.updated_at) : ''}
            </span>
          ),
        }),
        generateColumn({
          Header: (
            <TableSortableColumnHeader
              isActive={sort_by === 'creator'}
              activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
              accessor='creator'
              onClick={onTableHeaderClick}
              title='Added by'
            />
          ),
          accessor: 'creator',
          width: 200,
          Cell: ({ row }: { row: any }) => (
            <span className='text-bedrock-p text-bedrock-black'>{row.original.creator?.name || ''}</span>
          ),
        }),
        generateColumn({
          Header: 'Tags',
          accessor: 'tags',
          width: 132,
          allowShrinking: false,
          Cell: ({ row }: { row: any }) => {
            const tags = row.original.tags || [];
            const displayedTags = tags.slice(0, 2);
            const moreTagsCount = tags.length - 2;
            const moreTags = moreTagsCount > 0 ? tags.slice(2) : [];
            return (
              <div className='flex flex-row gap-2'>
                {displayedTags.map((tag: Tag) => (
                  <span key={tag.id} className='text-black block px-2 py-0.5 w-fit rounded-sm border bg-neue-canvas'>
                    {tag.name}
                  </span>
                ))}
                {moreTagsCount > 0 && (
                  <TooltipComponent
                    tooltipContent={
                      <>
                        {moreTags.map((tag: Tag) => (
                          <div key={tag.id}>{tag.name}</div>
                        ))}
                      </>
                    }
                    placement='bottom'
                    styling='tooltip'
                    theme='journey'
                  >
                    <span className='text-black block px-2 py-0.5 w-fit'>+{moreTagsCount}</span>
                  </TooltipComponent>
                )}
              </div>
            );
          },
        }),
      ];

      if (removable) {
        columns.push(
          generateColumn({
            Header: '',
            accessor: 'actions',
            width: 'auto',
            allowShrinking: false,
            Cell: ({ row }: { row: any }) => {
              return (
                <div className='text-bedrock-p text-bedrock-black'>
                  <div className='flex grow-0 justify-end'>
                    <LibraryContentCardMenuActions
                      contentId={(row.original as Content).id}
                      onContentDelete={onContentDeleted}
                      onTagsUpdate={onTagsUpdate}
                    />
                  </div>
                </div>
              );
            },
          })
        );
      }

      return columns;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort_by, sort_order, handleHeaderSelectAllRowsChange, toggleRowSelection]
  );

  const onPageChange = (e: any, newPage: number) => {
    hideLoadingSpinnerRef.current = false;
    scrollTopOnceFetchDoneRef.current = true;
    updatePage(newPage);
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner containerClassName={loadingClassName} />;
    }

    if (contents.length > 0) {
      return (
        <DataTable
          disableRowCursorPointer={disableRowCursorPointer}
          tableClassName={tableClassName}
          columnClasses={columnClasses}
          onRowClick={(row: any) => {
            if (onChooseExisting) {
              onChooseExisting(row.original);
            }
          }}
          columns={libraryColumns}
          data={contents}
        />
      );
    }

    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No content found</div>;
    }

    return (
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        Add your own content by clicking ‘Add content’, dragging your files here or connecting your Google Drive. You
        can add your content to new or existing Journey’s — or create a Journey by selecting your content.
      </div>
    );
  };

  return (
    <div className={`flex flex-col gap-4 ${containerClassName}`}>
      <ActionBar
        selectedCount={selectedRows.size}
        onClearSelection={onClearSelection}
        handleConfirmDelete={handleConfirmDelete}
        containerClasses='max-w-[649px] 2k:max-w-[808px]'
        confirmPanelCopy={`Are you sure you want to delete ${selectedRows.size} selected item(s)? Deleting them will remove them from the Journeys they are part of and is an irreversible action.`}
      />
      <div className={`h-full ${contentContainerClassName}`}>{renderContent()}</div>
      {pages > 1 && <Pagination count={pages} page={page} onChange={onPageChange} />}
    </div>
  );
};
